<template>
	<button
		ref="button"
		:class="[{disabled: disabled}, {'disable-box-shadow': disableBoxShadow}, {busy: busy}, `button-${appearance}`, intent, size]"
		:style="getStyle"
		:disabled="disableButton"
		:title="title"
		@mousedown="onMouseDown"
		@click="onClick"
		@focus="onFocus"
		@blur="onBlur"
		@keydown="onKeydown"
	>
		<t-timpex-icon
			v-if="iconBefore"
			:size="iconSize"
			:icon="iconBefore"
			class="before"
			type="bp"
		/>
		<div class="button-inner">
			<slot>
				{{value}}
			</slot>
		</div>
		<t-timpex-icon
			v-if="iconAfter"
			:size="iconSize"
			:icon="iconAfter"
			class="after"
			type="bp"
		/>
	</button>
</template>

<script>
import buttonMixin from './buttonMixin.js';
import TTimpexIcon from '../timpex/icons/TTimpexIcon';
export default {
	name: 'TButton',
	components: { TTimpexIcon },
	mixins: [buttonMixin],
	props: {
		/**
		 * Sets an icon before the text. Can be any icon from TTimpexIcon.
		 */
		iconBefore: String,
		/**
		 * Sets an icon after the text. Can be any icon from TTimpexIcon.
		 */
		iconAfter: String,
		/**
		 * Sets the width of the button.
		 */
		width: String,
		/**
		 * Text value of the button
		 */
		value: String,
		/**
		 * When true removes box-shadow (eg. when button is in focus)
		 */
		disableBoxShadow: Boolean,
		/**
		 * Text to show on hover
		 */
		title: String,
	},
	computed: {
		getStyle() {
			return {
				textAlign: (!this.iconBefore && !this.iconAfter ? 'center' : 'left'),
				width: this.width
			};
		},
		disableButton() {
			return this.disabled || this.busy;
		},
	}
};
</script>

<style lang="scss" scoped>
@import './scss/';

.button-inner {
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	flex:1;
}

.before, .after {
	pointer-events: none;
	flex-shrink: 0;
}

.before {
	margin-left: -3px;
	margin-right: 8px;
}

.after {
	margin-left: 8px;
	margin-right: -3px;
}
</style>


<docs>
## Examples

### Button Appearances

#### Default
```jsx
<div style="display: inline-grid; grid-column-gap: 10px; grid-template-columns: repeat(6, auto);">
	<t-button>Default</t-button>
	<t-button intent="success">Success</t-button>
	<t-button intent="warning">Warning</t-button>
	<t-button intent="danger">Danger</t-button>
	<t-button intent="danger" disabled>Disabled</t-button>
	<t-button intent="danger" busy>Busy</t-button>
</div>
```
#### Primary
```jsx
<div style="display: inline-grid; grid-column-gap: 10px; grid-template-columns: repeat(6, auto);">
	<t-button appearance="primary">Primary</t-button>
	<t-button appearance="primary" intent="success">Success</t-button>
	<t-button appearance="primary" intent="warning">Warning</t-button>
	<t-button appearance="primary" intent="danger">Danger</t-button>
	<t-button appearance="primary" intent="danger" disabled>Disabled</t-button>
	<t-button appearance="primary" intent="danger" busy>Busy</t-button>
</div>
```
#### Minimal
```jsx
<div style="display: inline-grid; grid-column-gap: 10px; grid-template-columns: repeat(6, auto);">
	<t-button appearance="minimal">Minimal</t-button>
	<t-button appearance="minimal" intent="success">Success</t-button>
	<t-button appearance="minimal" intent="warning">Warning</t-button>
	<t-button appearance="minimal" intent="danger">Danger</t-button>
	<t-button appearance="minimal" intent="danger" disabled>Disabled</t-button>
	<t-button appearance="minimal" intent="danger" busy>Busy</t-button>
</div>
```

### Slots

```jsx
<div style="display: inline-grid; grid-column-gap: 10px; grid-template-columns: repeat(3, auto);">
	<t-button appearance="minimal" iconBefore="user">Icon Before</t-button>
	<t-button appearance="minimal" iconAfter="caret-down">Icon After</t-button>
</div>
```
</docs>
